import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  RefreshCw,
  Search,
  Mail,
  Calendar,
  CreditCard,
  User,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Target,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  fetchUserGroup as apiFetchUserGroup,
  refreshGroupMemberCount,
  deleteUserGroup,
  getDetailedGroupMembers,
  UserGroup,
} from "../api/user-groups";
import { supabase } from "@/integrations/supabase/client";
import { usePagination } from "@/hooks/usePagination";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface GroupMember {
  user_id: number;
  name: string;
  email: string;
  avatar_url: string;
  created_at: string;
  subscription_status: string;
  subscription_plan_name: string;
  total_reading_seconds?: number;
  books_read_count?: number;
  highlights_count?: number;
  feedback_count?: number;
  audio_plays_count?: number;
  completion_rate?: number;
  account_age_days?: number;
  last_activity_days?: number;
}

export default function UserGroupDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const [userGroup, setUserGroup] = useState<UserGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    if (id) {
      fetchUserGroupDetails();
      fetchGroupMembers();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchGroupMembers();
    }
  }, [currentPage, id]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    if (searchTerm && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  const fetchUserGroupDetails = async () => {
    try {
      setLoading(true);
      const data = await apiFetchUserGroup(parseInt(id!));
      if (data) {
        setUserGroup(data);
      }
    } catch (error) {
      console.error("Error fetching user group:", error);
      toast({
        title: "Error",
        description: "Failed to fetch user group details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupMembers = async () => {
    try {
      setMembersLoading(true);

      // Use the new enhanced function to get detailed group members with pagination
      const offset = (currentPage - 1) * pageSize;
      const membersData = await getDetailedGroupMembers(parseInt(id!), pageSize, offset);
      setMembers(membersData);
    } catch (error) {
      console.error("Error fetching group members:", error);
      toast({
        title: "Error",
        description: "Failed to fetch group members.",
        variant: "destructive",
      });
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRefreshCount = async () => {
    if (!userGroup) return;

    try {
      setRefreshing(true);
      await refreshGroupMemberCount(userGroup.id);

      toast({
        title: "Success",
        description: "Member count refreshed successfully.",
      });

      // Refresh the group details and members, reset to page 1
      setCurrentPage(1);
      await fetchUserGroupDetails();
      await fetchGroupMembers();
    } catch (error) {
      console.error("Error refreshing member count:", error);
      toast({
        title: "Error",
        description: "Failed to refresh member count.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!userGroup) return;

    if (
      !confirm(
        "Are you sure you want to delete this user group? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteUserGroup(userGroup.id);

      toast({
        title: "Success",
        description: "User group deleted successfully.",
      });

      navigate("/admin/user-groups");
    } catch (error) {
      console.error("Error deleting user group:", error);
      toast({
        title: "Error",
        description: "Failed to delete user group.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatReadingTime = (seconds: number | undefined) => {
    if (!seconds || seconds === 0) return "0m";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getRelevantMetrics = (criteria: any) => {
    const metrics = [];

    if (
      criteria.total_reading_minutes ||
      criteria.books_read_count ||
      criteria.audio_plays_count
    ) {
      metrics.push("reading_time");
    }
    if (criteria.books_read_count) {
      metrics.push("books_read");
    }
    if (criteria.highlights_count) {
      metrics.push("highlights");
    }
    if (criteria.feedback_count) {
      metrics.push("feedback");
    }
    if (criteria.audio_plays_count) {
      metrics.push("audio_plays");
    }
    if (criteria.completion_rate) {
      metrics.push("completion_rate");
    }
    if (
      criteria.account_age_min_days ||
      criteria.account_age_max_days ||
      criteria.account_age_days
    ) {
      metrics.push("account_age");
    }
    if (criteria.last_activity_days) {
      metrics.push("last_activity");
    }

    return metrics;
  };

  const getMetricDisplay = (member: GroupMember, metrics: string[]) => {
    if (
      metrics.includes("reading_time") &&
      member.total_reading_seconds !== undefined
    ) {
      return {
        label: "Reading Time",
        value: formatReadingTime(member.total_reading_seconds),
        color: "text-blue-600 dark:text-blue-400",
      };
    }
    if (
      metrics.includes("books_read") &&
      member.books_read_count !== undefined
    ) {
      return {
        label: "Books Read",
        value: `${member.books_read_count}`,
        color: "text-green-600 dark:text-green-400",
      };
    }
    if (
      metrics.includes("highlights") &&
      member.highlights_count !== undefined
    ) {
      return {
        label: "Highlights",
        value: `${member.highlights_count}`,
        color: "text-purple-600 dark:text-purple-400",
      };
    }
    if (metrics.includes("feedback") && member.feedback_count !== undefined) {
      return {
        label: "Feedback",
        value: `${member.feedback_count}`,
        color: "text-orange-600 dark:text-orange-400",
      };
    }
    if (
      metrics.includes("audio_plays") &&
      member.audio_plays_count !== undefined
    ) {
      return {
        label: "Audio Plays",
        value: `${member.audio_plays_count}`,
        color: "text-indigo-600 dark:text-indigo-400",
      };
    }
    if (
      metrics.includes("completion_rate") &&
      member.completion_rate !== undefined
    ) {
      return {
        label: "Completion Rate",
        value: `${member.completion_rate}%`,
        color: "text-pink-600 dark:text-pink-400",
      };
    }
    if (
      metrics.includes("account_age") &&
      member.account_age_days !== undefined
    ) {
      return {
        label: "Account Age",
        value: `${member.account_age_days} days`,
        color: "text-cyan-600 dark:text-cyan-400",
      };
    }
    if (
      metrics.includes("last_activity") &&
      member.last_activity_days !== undefined
    ) {
      return {
        label: "Last Activity",
        value: `${member.last_activity_days} days ago`,
        color: "text-red-600 dark:text-red-400",
      };
    }

    // Default fallback
    return {
      label: "Reading Time",
      value: formatReadingTime(member.total_reading_seconds),
      color: "text-blue-600 dark:text-blue-400",
    };
  };

  const getSubscriptionBadgeVariant = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "trial":
        return "secondary";
      case "cancelled":
        return "destructive";
      case "free":
        return "outline";
      default:
        return "outline";
    }
  };

  const getSubscriptionBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "trial":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "cancelled":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "free":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const filteredMembers = members.filter(
    (member) =>
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalMembers = userGroup?.member_count || 0;
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: totalMembers,
    itemsPerPage: pageSize,
    currentPage,
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of members section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getGroupCategory = (groupName: string): string => {
    const categories = {
      "Content Consumption": [
        "Active Book Readers",
        "Audio Content Consumers",
        "Multi-Format Consumers",
        "Content Completion Champions",
      ],
      "Engagement & Interaction": [
        "Engaged Highlighters",
        "Feedback Providers",
        "Category Enthusiasts",
        "Content Discovery Users",
      ],
      "Subscription & Monetization": [
        "Subscription Holders",
        "Free Content Users",
        "Free Content Users with 30+ Minutes Reading",
        "Free Users - No Plan Ever",
        "Expired Subscribers",
        "Cancelled Subscribers",
        "Free Users with Account Age 30+ Days",
        "Free Users with Account Age 90+ Days",
        "Free Users with 5+ Books Read",
        "Free Users with 60+ Minutes Reading",
        "Free Users with 10+ Books Read",
        "Free Users with 2+ Hours Reading",
        "Expired Subscribers with 30+ Days Account Age",
        "Cancelled Subscribers with Reading Activity",
        "Free Users - High Engagement",
        "High-Value Subscribers",
        "At-Risk Churn Users",
      ],
      "User Lifecycle": [
        "Recent Registrations",
        "Inactive Users",
        "Engagement Leaders",
      ],
      "Demographic & Behavioral": [
        "Age Group Specific",
        "Referral Users",
        "Platform Specific Users",
      ],
      "Feature Adoption": ["Feature Adopters", "High Completion Rate Users"],
    };

    for (const [category, groups] of Object.entries(categories)) {
      if (groups.includes(groupName)) {
        return category;
      }
    }
    return "Other";
  };

  const getCategoryColor = (category: string): string => {
    const colors = {
      "Content Consumption":
        "text-blue-600 bg-blue-50 dark:bg-blue-950 dark:text-blue-300",
      "Engagement & Interaction":
        "text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-300",
      "Subscription & Monetization":
        "text-purple-600 bg-purple-50 dark:bg-purple-950 dark:text-purple-300",
      "User Lifecycle":
        "text-orange-600 bg-orange-50 dark:bg-orange-950 dark:text-orange-300",
      "Demographic & Behavioral":
        "text-indigo-600 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-300",
      "Feature Adoption":
        "text-pink-600 bg-pink-50 dark:bg-pink-950 dark:text-pink-300",
      Other: "text-gray-600 bg-gray-50 dark:bg-gray-950 dark:text-gray-300",
    };
    return colors[category] || colors["Other"];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  if (!userGroup) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h3 className="text-lg font-medium mb-2">User Group Not Found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The requested user group could not be found.
          </p>
          <Button onClick={() => navigate("/admin/user-groups")}>
            Back to User Groups
          </Button>
        </div>
      </div>
    );
  }

  const category = getGroupCategory(userGroup.name);
  const categoryColor = getCategoryColor(category);

  return (
    <AdminLayout title={`User Group: ${userGroup.name}`}>
      <div className="space-y-6">
        {/* Header with Back Button and Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/user-groups")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to User Groups
          </Button>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleRefreshCount}
              variant="outline"
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
              />
              {refreshing ? "Refreshing..." : "Refresh Count"}
            </Button>
            <Button
              asChild
              variant="outline"
              className="flex items-center gap-2"
            >
              <Link to={`/admin/user-groups/${userGroup.id}/edit`}>
                <Edit className="h-4 w-4" />
                Edit Group
              </Link>
            </Button>
            <Button
              onClick={handleDeleteGroup}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete Group
            </Button>
          </div>
        </div>

        {/* Group Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold">
                  {userGroup.name}
                </CardTitle>
                <CardDescription className="mt-2">
                  {userGroup.description || "No description provided"}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant={userGroup.is_active ? "default" : "secondary"}
                  className={
                    userGroup.is_active
                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                      : ""
                  }
                >
                  {userGroup.is_active ? "Active" : "Inactive"}
                </Badge>
                <Badge variant="outline" className={categoryColor}>
                  {category}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Member Count */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Members
                  </p>
                  <p className="text-2xl font-bold">
                    {userGroup.member_count.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Last Refreshed */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <RefreshCw className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Last Refreshed
                  </p>
                  <p className="text-sm font-semibold">
                    {userGroup.last_refreshed_at
                      ? formatDate(userGroup.last_refreshed_at)
                      : "Never"}
                  </p>
                </div>
              </div>

              {/* Created */}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Calendar className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Created
                  </p>
                  <p className="text-sm font-semibold">
                    {formatDate(userGroup.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Criteria */}
            {userGroup.criteria &&
              Object.keys(userGroup.criteria).length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                    Targeting Criteria
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(userGroup.criteria).map(([key, value]) => (
                      <Badge key={key} variant="outline" className="text-sm">
                        {key}: {String(value)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>

        {/* Members Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-semibold">
                  Group Members
                </CardTitle>
                <CardDescription>
                  {searchTerm 
                    ? `Showing ${filteredMembers.length} of ${members.length} members on this page`
                    : `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalMembers)} - ${Math.min(currentPage * pageSize, totalMembers)} of ${totalMembers.toLocaleString()} total members`}
                </CardDescription>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
                <Input
                  placeholder="Search members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {membersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  No members found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchTerm
                    ? "Try adjusting your search criteria."
                    : "This group currently has no members matching the criteria."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMembers.map((member) => {
                  const relevantMetrics = getRelevantMetrics(
                    userGroup?.criteria || {}
                  );
                  const primaryMetric = getMetricDisplay(
                    member,
                    relevantMetrics
                  );

                  return (
                    <div
                      key={member.user_id}
                      onClick={() => navigate(`/admin/users/${member.user_id}`)}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                          {member.avatar_url ? (
                            <img
                              src={member.avatar_url}
                              alt={member.name}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <User className="h-5 w-5 text-gray-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                            {member.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {member.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {primaryMetric.label}
                          </p>
                          <p
                            className={`text-sm font-medium ${primaryMetric.color}`}
                          >
                            {primaryMetric.value}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Joined
                          </p>
                          <p className="text-sm font-medium">
                            {formatDate(member.created_at)}
                          </p>
                        </div>
                        <Badge
                          variant={getSubscriptionBadgeVariant(
                            member.subscription_status
                          )}
                          className={getSubscriptionBadgeColor(
                            member.subscription_status
                          )}
                        >
                          {member.subscription_status}
                        </Badge>
                        {member.subscription_plan_name !== "No Plan" && (
                          <Badge variant="outline" className="text-xs">
                            {member.subscription_plan_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {!searchTerm && totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing{" "}
                  <span className="font-medium">
                    {totalMembers === 0
                      ? 0
                      : (currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalMembers)}
                  </span>{" "}
                  of <span className="font-medium">{totalMembers.toLocaleString()}</span>{" "}
                  members
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(1)}
                    disabled={!canGoPrev}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={!canGoPrev}
                  >
                    Previous
                  </Button>
                  
                  <Pagination>
                    <PaginationContent>
                      {pageNumbers.map((pageNum) => (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => goToPage(pageNum)}
                            isActive={pageNum === currentPage}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                    </PaginationContent>
                  </Pagination>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={!canGoNext}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => goToPage(totalPages)}
                    disabled={!canGoNext}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
